"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, KeyRound, CheckCircle2 } from "lucide-react";

export function RollApiKeyModal({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const handleRollKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6) {
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setOpen(false);
        setCode("");
      }, 2000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" /> Roll Access Keys
          </DialogTitle>
          <DialogDescription>
            You are about to invalidate your current Secret Key. This action is <strong className="text-foreground">irreversible</strong> and will immediately break any active integrations using the old key.
          </DialogDescription>
        </DialogHeader>
        
        {isSuccess ? (
          <div className="py-6 flex flex-col items-center justify-center text-center space-y-3 animate-in zoom-in-95 duration-300">
            <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h3 className="font-bold text-lg text-emerald-600">Keys Regenerated!</h3>
              <p className="text-sm text-muted-foreground">Your new Secret Key is now active.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleRollKey} className="space-y-6 py-4 animate-in fade-in duration-300">
            <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-destructive/20">
              <Label htmlFor="2fa-code" className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-primary" /> Two-Factor Authentication
              </Label>
              <p className="text-xs text-muted-foreground">Please enter the 6-digit code from your authenticator app to authorize this critical action.</p>
              <Input
                id="2fa-code"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="h-12 text-center text-2xl font-mono tracking-[0.5em] focus-visible:ring-destructive"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" variant="destructive" disabled={code.length !== 6}>Authorize & Regenerate</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
